'use client';
import { Node } from '@tiptap/core';
import { type NodeViewProps, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import { Box } from '@mui/material';
import { useEffect, useRef } from 'react';

export default function MathBlockComponent(props: NodeViewProps) {
  const { node } = props;
  const formula = node.attrs.formula || '';
  const displayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (displayRef.current && formula) {
      try {
        katex.render(formula, displayRef.current, {
          displayMode: true,
          throwOnError: false,
          strict: 'ignore',
        });
      } catch (error) {
        console.error('LaTeX rendering failed (math-block):', {
          formula,
          error: error instanceof Error ? error.message : String(error),
        });

        if (displayRef.current) {
          displayRef.current.innerHTML = `
            <span style="color: #f44336; font-family: monospace; font-size: 0.9em;">
              LaTeX rendering failed: ${formula}
            </span>
          `;
        }
      }
    }
  }, [formula]);

  if (!formula) return null;

  return (
    <NodeViewWrapper className="math-block" data-type="math-block">
      <Box
        sx={(theme) => ({
          border: `1px dashed ${theme.palette.divider}`,
          borderRadius: theme.shape.borderRadius,
          backgroundColor: theme.palette.background.default,
          px: 3,
          my: 2,
          textAlign: 'center',
          overflowX: 'auto',
        })}>
        <Box
          component="div"
          ref={displayRef}
          sx={(theme) => ({
            fontSize: theme.typography.body1.fontSize,
            lineHeight: 1.5,
            color: theme.palette.text.primary,
          })}
        />
      </Box>
    </NodeViewWrapper>
  );
}

export const MathBlock = Node.create({
  name: 'math_block',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      formula: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-formula') || '',
        renderHTML: (attrs: { formula: string }) => ({
          'data-type': 'math-block',
          'data-formula': attrs.formula,
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="math-block"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', HTMLAttributes];
  },

  addNodeView() {
    return ReactNodeViewRenderer(MathBlockComponent);
  },
});
